import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, FileDown, Send, Loader2, RefreshCw, Copy } from "lucide-react";
import api from "../lib/api";
import { isNoldDomain } from "../lib/domain";
import { useZone } from "../context/ZoneContext";

/**
 * Générateur de Courrier Intelligent — encart Admin.
 * L'admin saisit son idée, choisit le ton, éventuellement le destinataire ;
 * l'IA rédige, l'admin corrige, puis imprime en PDF ou envoie par email.
 */
export const MailGenerator = () => {
  const { zone } = useZone();
  const isNold = zone === "europe" || isNoldDomain();
  const brandKey = isNold ? "nold" : "bentskin";
  const brandLabel = isNold ? "Nold Go" : "BentSkin Go";

  const [idea, setIdea] = useState("");
  const [tone, setTone] = useState("formal");
  const [role, setRole] = useState("partenaire");
  const [recipient, setRecipient] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [brandInfo, setBrandInfo] = useState(null);

  const generate = async () => {
    if (!idea.trim()) { toast.error("Décrivez l'idée du courrier."); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/mail-generator/generate", {
        idea: idea.trim(), tone, recipient_role: role,
        recipient_name: recipient.trim(), brand: brandKey,
      });
      setSubject(r.data.subject || "");
      setBody(r.data.body || "");
      setBrandInfo(r.data.brand || null);
      toast.success("Courrier généré, vérifiez et ajustez si besoin.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération impossible.");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!body.trim()) { toast.error("Générez d'abord le courrier."); return; }
    setPdfBusy(true);
    try {
      const r = await api.post("/admin/mail-generator/pdf",
        { subject, body, recipient_name: recipient.trim(), brand: brandKey },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `courrier-${brandLabel.toLowerCase().replace(" ", "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé.");
    } catch (e) {
      toast.error("Génération PDF impossible.");
    } finally { setPdfBusy(false); }
  };

  const sendEmail = async () => {
    if (!toEmail.trim() || !toEmail.includes("@")) { toast.error("Renseignez l'email du destinataire."); return; }
    if (!body.trim() || !subject.trim()) { toast.error("Générez d'abord le courrier."); return; }
    setSendBusy(true);
    try {
      await api.post("/admin/mail-generator/send", { to: toEmail.trim(), subject, body });
      toast.success(`Email envoyé à ${toEmail.trim()}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi email impossible.");
    } finally { setSendBusy(false); }
  };

  const copyToClipboard = async () => {
    if (!body) return;
    const full = `Objet : ${subject}\n\n${body}`;
    try { await navigator.clipboard.writeText(full); toast.success("Copié !"); }
    catch { toast.error("Impossible de copier"); }
  };

  return (
    <section className="bg-white rounded-3xl border border-[#E2DFD8] p-6" data-testid="mail-generator">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="font-head font-black text-lg text-[#1A1A1A]">Générateur de Courrier IA</div>
          <div className="text-xs text-[#75807A]">Rédige, imprime, envoie · marque {brandLabel}</div>
        </div>
      </div>

      {/* Formulaire d'idée */}
      <div className="grid gap-3">
        <div>
          <label className="text-xs font-semibold text-[#3B423E] mb-1 block">Votre idée <span className="text-[#D94F2B]">*</span></label>
          <textarea data-testid="mailgen-idea" value={idea} onChange={(e) => setIdea(e.target.value)} rows={3}
            placeholder="Ex : Remercier un chauffeur pour son professionnalisme et lui annoncer sa prime mensuelle"
            className="w-full rounded-xl border border-[#E2DFD8] px-3 py-2.5 text-sm focus:outline-none focus:border-[#F26419] resize-none" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-[#3B423E] mb-1 block">Ton</label>
            <select data-testid="mailgen-tone" value={tone} onChange={(e) => setTone(e.target.value)}
              className="w-full h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm focus:outline-none focus:border-[#F26419] bg-white">
              <option value="formal">Formel</option>
              <option value="friendly">Amical</option>
              <option value="administrative">Administratif</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#3B423E] mb-1 block">Destinataire</label>
            <select data-testid="mailgen-role" value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm focus:outline-none focus:border-[#F26419] bg-white">
              <option value="partenaire">Partenaire (entreprise)</option>
              <option value="chauffeur">Chauffeur</option>
              <option value="passager">Client / Passager</option>
              <option value="fournisseur">Fournisseur</option>
              <option value="autre">Autre</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#3B423E] mb-1 block">Nom du destinataire (facultatif)</label>
          <input data-testid="mailgen-recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)}
            placeholder="M. Olivier Parent"
            className="w-full h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm focus:outline-none focus:border-[#F26419]" />
        </div>
        <button data-testid="mailgen-generate" onClick={generate} disabled={busy}
          className="w-full h-12 rounded-xl bg-[#F26419] hover:bg-[#D94F2B] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
          {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Génération…</> : <><Sparkles className="w-5 h-5" /> Générer avec l'IA</>}
        </button>
      </div>

      {/* Résultat */}
      {(subject || body) && (
        <div className="mt-6 border-t border-[#E2DFD8] pt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold text-[#75807A] uppercase tracking-wide">Courrier généré (modifiable)</div>
            <div className="flex gap-2">
              <button data-testid="mailgen-copy" onClick={copyToClipboard} className="text-xs text-[#3B423E] hover:text-[#F26419] flex items-center gap-1"><Copy className="w-3 h-3" /> Copier</button>
              <button data-testid="mailgen-regen" onClick={generate} disabled={busy} className="text-xs text-[#3B423E] hover:text-[#F26419] flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Regénérer</button>
            </div>
          </div>
          <div className="grid gap-3">
            <div>
              <label className="text-xs font-semibold text-[#3B423E] mb-1 block">Objet</label>
              <input data-testid="mailgen-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                className="w-full h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm font-semibold focus:outline-none focus:border-[#F26419]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#3B423E] mb-1 block">Corps du courrier</label>
              <textarea data-testid="mailgen-body" value={body} onChange={(e) => setBody(e.target.value)} rows={10}
                className="w-full rounded-xl border border-[#E2DFD8] px-3 py-2.5 text-sm leading-relaxed focus:outline-none focus:border-[#F26419] resize-y font-serif" />
            </div>
          </div>

          {/* Preview signature/pied */}
          {brandInfo && (
            <div className="mt-4 bg-[#F4F0EA] rounded-xl p-4 text-xs text-[#3B423E]">
              <div className="font-bold mb-1">Signature ajoutée automatiquement :</div>
              <div>Cordialement,</div>
              <div className="font-semibold">{brandInfo.signature_name}</div>
              <div className="text-[#75807A]">{brandInfo.name} · {brandInfo.address}</div>
              <div className="text-[#75807A]">{brandInfo.phone} · {brandInfo.email} · {brandInfo.site}</div>
            </div>
          )}

          {/* Actions PDF / Email */}
          <div className="mt-6 grid gap-3">
            <button data-testid="mailgen-pdf" onClick={downloadPdf} disabled={pdfBusy}
              className="h-11 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50">
              {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Imprimer en PDF
            </button>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <input data-testid="mailgen-to" type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                placeholder="destinataire@email.com"
                className="h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm focus:outline-none focus:border-[#F26419]" />
              <button data-testid="mailgen-send" onClick={sendEmail} disabled={sendBusy}
                className="h-11 px-5 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold flex items-center gap-2 disabled:opacity-50">
                {sendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer
              </button>
            </div>
            <div className="text-[11px] text-[#75807A] text-center">
              Yahoo/AOL sont bloqués par notre service. Utilisez Gmail, Outlook, iCloud ou pro.
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
